// Native (browser) upload compression pipeline.
//
// demux+decode (mediabunny Input/VideoSampleSink) -> real 30fps normalization (FrameScheduler)
// -> letterbox scale (OffscreenCanvas, rotation-aware) -> encode (WebCodecs VideoEncoder)
// -> mux (mediabunny) -> bounded size/resolution retry (VideoCompressionPolicy).
//
// This is the upload-side counterpart to WebCodecsExporter.ts (which handles download/export of
// possibly-multi-slice, already-cached video). Unlike that path, this one processes a single
// arbitrary source File that may use any source frame rate, so it needs genuine frame-rate
// normalization and a truthful, size-bounded retry policy instead of a fixed bitrate. The two
// pipelines share the same windowed-decode/backpressure shape by design (see decodeWindow below),
// but are kept as separate modules rather than forced into one shared framework, since the
// scheduling and retry concerns here don't apply to the download path.
import { debug } from 'debug'
import {
    Input,
    ALL_FORMATS,
    BlobSource,
    VideoSampleSink,
    Output,
    Mp4OutputFormat,
    BufferTarget,
    EncodedVideoPacketSource,
    EncodedPacket,
} from 'mediabunny'
import { t } from 'ttag'
import { FrameScheduler } from './FrameScheduler'
import {
    NativeTargetResolution,
    NativeAttempt,
    targetDimensions,
    isNativeSizeBudgetFeasible,
    firstNativeAttempt,
    nextNativeAttempt,
} from './VideoCompressionPolicy'

const log = debug('sltt:NativeVideoTranscoder')

export const OUT_FPS = 30
const GOP_SIZE = OUT_FPS * 2
const TRIM_EPSILON = 0.001
// Matches WebCodecsExporter's decode-window strategy: resets the hardware decoder periodically
// so long videos don't exhaust GPU resources.
const MAX_DECODE_CHUNK_SEC = 30
const AVC_CODEC_STRING = 'avc1.42E028'
// Defensive backstop for the retry loop below. The duplicate-free attempt sequence in
// VideoCompressionPolicy is itself finite and terminates well before this many attempts; this
// bound only guards against a future change to that policy accidentally looping forever.
const MAX_ATTEMPTS = 8

export type NativeTranscodeProgressFn = (pct: number, message: string) => void

export interface NativeCompressionMetadata {
    engine: 'webcodecs'
    width: number
    height: number
    resolution: NativeTargetResolution
    frameRate: number
    bitrate: number
    codec: string
}

export interface NativeCompressionResult {
    file: File
    metadata: NativeCompressionMetadata
}

// Thrown when the input cannot be handled by the native pipeline at all (wrong codec/container,
// the browser lacks the necessary decode/encode capability, or the configured size budget is
// mathematically unreachable). Callers should silently fall back to the external compressor.
export class NativeCompressionUnsupportedError extends Error {
    constructor(message: string) {
        super(message)
        this.name = 'NativeCompressionUnsupportedError'
    }
}

// Thrown when native processing looked supported but failed at runtime (decode/encode/mux
// error). Callers should clean up and fall back to the external compressor when eligible.
export class NativeCompressionFailedError extends Error {
    constructor(message: string, public readonly cause?: unknown) {
        super(message)
        this.name = 'NativeCompressionFailedError'
    }
}

// Thrown when the bounded native retry strategy is exhausted without producing a file within
// maxVideoSizeMB (including when the budget is preflight-infeasible). Callers should fall back
// to the external compressor as the final attempt.
export class NativeCompressionTooLargeError extends Error {
    constructor(message: string) {
        super(message)
        this.name = 'NativeCompressionTooLargeError'
    }
}

function buildEncoderConfig(resolution: NativeTargetResolution, bitrateBps: number): VideoEncoderConfig {
    const { width, height } = targetDimensions(resolution)
    return {
        codec: AVC_CODEC_STRING,
        width,
        height,
        bitrate: bitrateBps,
        framerate: OUT_FPS,
        latencyMode: 'quality',
        avc: { format: 'avc' },
    }
}

// Checks the *exact* configuration that will be passed to encoder.configure() — not just generic
// AVC/codec-existence support — via the standards VideoEncoder.isConfigSupported() static method.
async function isExactEncoderConfigSupported(config: VideoEncoderConfig): Promise<boolean> {
    try {
        const result = await VideoEncoder.isConfigSupported(config)
        return result.supported === true
    } catch (error) {
        log('isExactEncoderConfigSupported check failed', error)
        return false
    }
}

// Runtime capability check for a candidate source file: does NOT contact the external
// compressor, and does NOT decide encoder capability. This answers only whether WebCodecs
// exists and the source's primary video track can be demuxed/decoded natively — routing (native
// vs external) must not depend on whether one particular *output* resolution's encoder
// configuration happens to be supported, since a browser/device that can't encode 720p H.264 may
// still be able to encode 480p. Each real attempt's exact encoder configuration (codec, exact
// width/height, exact bitrate) is checked separately inside compressVideoNative, where the
// native retry sequence can advance past an unsupported resolution instead of aborting outright.
export async function isNativeCompressionSupported(file: File): Promise<boolean> {
    if (typeof VideoDecoder === 'undefined' || typeof VideoEncoder === 'undefined') return false

    let input: Input | undefined
    try {
        input = new Input({ formats: ALL_FORMATS, source: new BlobSource(file) })
        const videoTrack = await input.getPrimaryVideoTrack()
        if (!videoTrack) return false

        return await videoTrack.canDecode()
    } catch (error) {
        log('isNativeCompressionSupported check failed', error)
        return false
    } finally {
        input?.dispose()
    }
}

export interface NativeCompressOptions {
    maxVideoSizeMB: number
    // Project's configured compressedVideoResolution (720 falls back to 480 on retry, per the
    // existing external-compressor semantics; any value <= 480 starts at 480 directly).
    preferredResolution: number
    onProgress?: NativeTranscodeProgressFn
}

// Compresses `file` entirely in-browser and returns the resulting MP4 File plus truthful
// compression metadata. Never contacts the external compressor. Throws one of the typed errors
// above when native compression cannot produce a usable result, so the caller (VideoDropTarget)
// can decide whether/how to fall back.
export async function compressVideoNative(file: File, options: NativeCompressOptions): Promise<NativeCompressionResult> {
    const { maxVideoSizeMB, onProgress = () => {} } = options
    const preferredResolution: NativeTargetResolution = options.preferredResolution > 480 ? 720 : 480

    if (typeof VideoDecoder === 'undefined' || typeof VideoEncoder === 'undefined') {
        throw new NativeCompressionUnsupportedError('WebCodecs is not supported in this browser')
    }

    onProgress(0, t`Analyzing video...`)

    // Authoritative video timeline: scoped to the primary video track only (never the whole
    // Input, which would let a longer/shorter audio track or approximate container metadata
    // corrupt a video-only output), using Mediabunny's precise (non-metadata-estimate) timing
    // APIs. getFirstTimestamp/computeDuration return *absolute* track timestamps — a track does
    // not necessarily start at zero — so both start and end come from the same track query.
    //
    // rawVideoStartSec can in principle be negative (decoder priming/edit-list artifacts before
    // the conventional zero point). presentationStartSec clamps that to a non-negative timeline
    // origin: negative-timestamp preroll must never become visible output content, so it is
    // excluded from both the presented duration and the normalized timeline used everywhere else
    // (decode-window ranges, FrameScheduler input). A genuinely positive non-zero start is left
    // untouched — only negative raw starts are affected by the clamp.
    let rawVideoStartSec: number
    let presentationStartSec: number
    let durationSec: number
    const probeInput = new Input({ formats: ALL_FORMATS, source: new BlobSource(file) })
    try {
        const probeTrack = await probeInput.getPrimaryVideoTrack()
        if (!probeTrack) {
            throw new NativeCompressionUnsupportedError('No video track found in file')
        }

        const canDecode = await probeTrack.canDecode()
        if (!canDecode) {
            throw new NativeCompressionUnsupportedError('Video track codec is not decodable in this browser')
        }

        rawVideoStartSec = await probeInput.getFirstTimestamp([probeTrack])
        presentationStartSec = Math.max(0, rawVideoStartSec)
        const videoEndSec = await probeInput.computeDuration([probeTrack])
        durationSec = videoEndSec - presentationStartSec

        if (!(durationSec > 0) || !isFinite(durationSec)) {
            throw new NativeCompressionFailedError(
                `Could not determine a valid video-track duration (start=${rawVideoStartSec}, presentationStart=${presentationStartSec}, end=${videoEndSec})`,
            )
        }
    } finally {
        probeInput.dispose()
    }

    // Preflight: if even the bitrate floor can't hit maxVideoSizeMB for this duration, no
    // resolution/bitrate combination the retry policy could try will ever succeed. Fail closed
    // to the external compressor now rather than running one or more full transcodes that are
    // guaranteed to come back oversized.
    if (!isNativeSizeBudgetFeasible(durationSec, maxVideoSizeMB)) {
        throw new NativeCompressionTooLargeError(
            `Requested maxVideoSizeMB=${maxVideoSizeMB} is not reachable for a ${durationSec.toFixed(1)}s video even at the bitrate floor`,
        )
    }

    let attempt: NativeAttempt | null = firstNativeAttempt(durationSec, maxVideoSizeMB, preferredResolution)
    let attemptCount = 0
    // Tracks whether *any* attempt in the sequence had a supported encoder configuration, so the
    // final error can distinguish "no browser-supported encoder configuration exists at any
    // resolution this policy would try" from "every supported attempt produced an oversized
    // file" — both fall back to the external compressor identically, but the distinction matters
    // for honest diagnostics.
    let sawSupportedEncoderConfig = false

    while (attempt !== null) {
        if (++attemptCount > MAX_ATTEMPTS) {
            // The duplicate-free attempt sequence is itself finite and bounded well below
            // MAX_ATTEMPTS; this is a defensive backstop, not the expected termination path.
            break
        }

        const { resolution, bitrateStep } = attempt.state
        const { bitrateBps } = attempt

        log('compressVideoNative attempt', { attemptCount, resolution, bitrateStep, bitrateBps, durationSec, presentationStartSec })

        const encoderConfig = buildEncoderConfig(resolution, bitrateBps)
        const encoderSupported = await isExactEncoderConfigSupported(encoderConfig)
        if (!encoderSupported) {
            // This exact (resolution, bitrate) encoder configuration isn't supported — but a
            // *different* resolution (e.g. 480p when 720p is unsupported) may still work, so
            // advance through the same finite retry sequence rather than aborting the whole
            // native path immediately.
            log('compressVideoNative: encoder configuration unsupported, advancing to next attempt', { resolution, bitrateBps })
            attempt = nextNativeAttempt(durationSec, maxVideoSizeMB, attempt)
            continue
        }
        sawSupportedEncoderConfig = true

        let outputFile: File
        try {
            outputFile = await transcodeToMp4(file, presentationStartSec, durationSec, encoderConfig, onProgress)
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error)
            throw new NativeCompressionFailedError(`Native transcode failed: ${message}`, error)
        }

        const sizeMB = outputFile.size / (1024 * 1024)
        log('compressVideoNative result', { resolution, bitrateBps, sizeMB })

        if (sizeMB <= maxVideoSizeMB) {
            onProgress(1, '')
            return {
                file: outputFile,
                metadata: {
                    engine: 'webcodecs',
                    width: encoderConfig.width,
                    height: encoderConfig.height,
                    resolution,
                    frameRate: OUT_FPS,
                    bitrate: bitrateBps,
                    codec: AVC_CODEC_STRING,
                },
            }
        }

        attempt = nextNativeAttempt(durationSec, maxVideoSizeMB, attempt)
    }

    if (!sawSupportedEncoderConfig) {
        throw new NativeCompressionUnsupportedError(
            `No supported H.264 encoder configuration found across ${attemptCount} native attempt(s)`,
        )
    }
    throw new NativeCompressionTooLargeError(`Could not compress video below ${maxVideoSizeMB}MB using native compression`)
}

type ChunkEntry = { chunk: EncodedVideoChunk; meta: EncodedVideoChunkMetadata | undefined }

// Mutable box the caller polls after encoder.flush() to detect a fatal WebCodecs encoder error.
// VideoEncoder's `error` callback fires asynchronously outside the awaited call stack, so
// throwing directly inside it would not reliably reject the enclosing transcode promise; per the
// WebCodecs spec a fatal encoder error should already reject pending flush()/encode() promises,
// but this is an explicit, browser-independent safety net so a failure can never silently vanish.
interface EncoderErrorBox {
    error: Error | null
}

function throwIfEncoderErrored(box: EncoderErrorBox) {
    if (box.error) throw box.error
}

// Waits for encoder.encodeQueueSize to drop to `threshold` or below. Uses VideoEncoder's own
// spec-defined `dequeue` event (fired whenever the codec's internal queue shrinks) rather than a
// setTimeout/rAF polling loop: background/hidden tabs are heavily throttled by browsers (Chrome
// clamps setTimeout to roughly once per second once backgrounded), which would otherwise stall
// this entire loop — and therefore the whole transcode — whenever the tab loses focus. `dequeue`
// is driven by the codec's own internal processing (potentially off-thread), not a JS timer, so
// it is not subject to that throttling.
export function waitForEncodeQueueBelow(encoder: VideoEncoder, threshold: number): Promise<void> {
    if (encoder.encodeQueueSize <= threshold) return Promise.resolve()

    return new Promise((resolve) => {
        const onDequeue = () => {
            if (encoder.encodeQueueSize <= threshold) {
                encoder.removeEventListener('dequeue', onDequeue)
                resolve()
            }
        }
        encoder.addEventListener('dequeue', onDequeue)
    })
}

// Mediabunny's VideoSampleSink.samples(start, end) uses true half-open range semantics: start is
// inclusive, end is exclusive. So adjacent windows can tile the presentation timeline exactly —
// window N's rangeEnd equals window N+1's rangeStart — with zero overlap and no epsilon fudging
// at internal boundaries: a sample exactly on a 30-second boundary belongs to exactly one window
// (the one it starts). The only float-precision risk is at the very last window's end, where
// durationSec was derived by subtracting and re-adding presentationStartSec; a tiny epsilon there
// guards against excluding the true final sample due to rounding. Exported for direct testing of
// this boundary math without needing a real decode pipeline.
export function computeWindowRange(presentationStartSec: number, windowStart: number, windowEnd: number, isLastWindow: boolean) {
    return {
        rangeStart: presentationStartSec + windowStart,
        rangeEnd: presentationStartSec + windowEnd + (isLastWindow ? TRIM_EPSILON : 0),
    }
}

async function decodeWindow(
    file: File,
    presentationStartSec: number,
    windowStart: number,
    windowEnd: number,
    isLastWindow: boolean,
    encoder: VideoEncoder,
    encoderErrorBox: EncoderErrorBox,
    scheduler: FrameScheduler,
    canvas: OffscreenCanvas,
    ctx: OffscreenCanvasRenderingContext2D,
    forceKeyFrame: boolean,
    onProgress: NativeTranscodeProgressFn,
    windowIndex: number,
    windowCount: number,
): Promise<void> {
    const input = new Input({ formats: ALL_FORMATS, source: new BlobSource(file) })
    try {
        const videoTrack = await input.getPrimaryVideoTrack()
        if (!videoTrack) throw new Error('No video track found in file')

        const canDecode = await videoTrack.canDecode()
        if (!canDecode) throw new Error('Video track codec is not decodable in this browser')

        const sink = new VideoSampleSink(videoTrack)

        // windowStart/windowEnd are relative to the normalized (presentation-origin) timeline;
        // Mediabunny sample timestamps are absolute, so the sink range must be re-offset by
        // presentationStartSec. See computeWindowRange for why this doesn't overlap adjacent
        // windows.
        const { rangeStart, rangeEnd } = computeWindowRange(presentationStartSec, windowStart, windowEnd, isLastWindow)
        const windowSpan = Math.max(TRIM_EPSILON, windowEnd - windowStart)

        let sampleIndex = 0
        let outputIndex = 0

        const emit = (timestampUs: number, durationUs: number) => {
            const isKeyFrame = (forceKeyFrame && outputIndex === 0) || outputIndex % GOP_SIZE === 0
            const frame = new VideoFrame(canvas, { timestamp: timestampUs, duration: durationUs })
            try {
                encoder.encode(frame, { keyFrame: isKeyFrame })
            } finally {
                frame.close()
            }
            outputIndex++
        }

        try {
            for await (const sample of sink.samples(rangeStart, rangeEnd)) {
                try {
                    throwIfEncoderErrored(encoderErrorBox)

                    const normalizedTimestamp = sample.timestamp - presentationStartSec
                    if (normalizedTimestamp < windowStart - TRIM_EPSILON) {
                        continue
                    }

                    // Backpressure: yield if the encoder queue is growing to avoid GPU memory buildup.
                    // See waitForEncodeQueueBelow — this must not stall in a backgrounded tab.
                    while (encoder.encodeQueueSize > 10) {
                        throwIfEncoderErrored(encoderErrorBox)
                        await waitForEncodeQueueBelow(encoder, 10)
                    }

                    for (const scheduled of scheduler.push(sampleIndex, normalizedTimestamp)) {
                        emit(scheduled.timestampUs, scheduled.durationUs)
                    }

                    ctx.fillStyle = 'black'
                    ctx.fillRect(0, 0, canvas.width, canvas.height)
                    // drawWithFit({ fit: 'contain' }) preserves aspect ratio, letterboxes into the
                    // full canvas, and takes the sample's rotation metadata into account.
                    sample.drawWithFit(ctx, { fit: 'contain' })

                    const windowProgress = Math.min(1, Math.max(0, (normalizedTimestamp - windowStart) / windowSpan))
                    const overall = Math.min(1, ((windowIndex - 1) + windowProgress) / windowCount)
                    onProgress(overall, t`Compressing video...` + ` ${Math.round(overall * 100)}%`)

                    sampleIndex++
                } finally {
                    sample.close()
                }
            }

            if (isLastWindow) {
                throwIfEncoderErrored(encoderErrorBox)
                for (const scheduled of scheduler.finish(windowEnd)) {
                    emit(scheduled.timestampUs, scheduled.durationUs)
                }
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error)
            const wrapped = new Error(
                `Native decode failed for window [${windowStart}-${windowEnd}], fileSize=${file.size}: ${message}`,
            )
            ;(wrapped as any).cause = error
            throw wrapped
        }
    } finally {
        input.dispose()
    }
}

async function transcodeToMp4(
    file: File,
    presentationStartSec: number,
    durationSec: number,
    encoderConfig: VideoEncoderConfig,
    onProgress: NativeTranscodeProgressFn,
): Promise<File> {
    const source = new EncodedVideoPacketSource('avc')
    const bufferTarget = new BufferTarget()
    const output = new Output({
        format: new Mp4OutputFormat({ fastStart: 'in-memory' }),
        target: bufferTarget,
    })
    output.addVideoTrack(source)
    await output.start()

    const segmentChunks: ChunkEntry[] = []
    const encoderErrorBox: EncoderErrorBox = { error: null }
    const encoder = new VideoEncoder({
        output: (chunk, meta) => segmentChunks.push({ chunk, meta }),
        error: (e) => {
            // See EncoderErrorBox above: record the error so it can be deterministically
            // re-thrown on the awaited call stack instead of only surfacing here.
            encoderErrorBox.error = e instanceof Error ? e : new Error(String(e))
        },
    })
    encoder.configure(encoderConfig)

    const canvas = new OffscreenCanvas(encoderConfig.width, encoderConfig.height)
    const ctx = canvas.getContext('2d')!
    const scheduler = new FrameScheduler(OUT_FPS)

    const windowCount = Math.max(1, Math.ceil(durationSec / MAX_DECODE_CHUNK_SEC))
    let windowStart = 0
    let windowIndex = 0
    let isFirstWindow = true

    try {
        while (windowStart < durationSec - TRIM_EPSILON) {
            const chunkEnd = Math.min(windowStart + MAX_DECODE_CHUNK_SEC, durationSec)
            windowIndex += 1
            const isLastWindow = chunkEnd >= durationSec - TRIM_EPSILON

            segmentChunks.length = 0
            await decodeWindow(
                file, presentationStartSec, windowStart, chunkEnd, isLastWindow,
                encoder, encoderErrorBox, scheduler, canvas, ctx,
                !isFirstWindow, onProgress, windowIndex, windowCount,
            )

            await encoder.flush()
            throwIfEncoderErrored(encoderErrorBox)
            for (const { chunk, meta } of segmentChunks) {
                await source.add(EncodedPacket.fromEncodedChunk(chunk), meta)
            }

            windowStart = chunkEnd
            isFirstWindow = false
        }
    } finally {
        encoder.close()
    }

    await output.finalize()

    return new File([bufferTarget.buffer!], 'sltt-video-compressed.mp4', { type: 'video/mp4' })
}
